import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWidgetData } from '../../hooks/useWidgetData';
import {
  FeedQueryResult,
  LightboxItem,
  LightboxLanguageText,
} from '../../net/softbox-types';
import { getSSRTranslation } from '@/i18n.ts';
import JokesSwipeWrapper from './JokesSwipeWrapper';
import ActivityIndicator from '@moment/components/ActivityIndicator/ActivityIndicator.tsx';

interface Joke {
  id: string | number;
  setup: string;
  punchline: string;
}

interface ApiJokeItem extends LightboxItem {
  joke?: {
    setup: LightboxLanguageText;
    punchline: LightboxLanguageText;
  };
}

interface JokesSwipeWrapperContainerProps {
  feedType?: string;
}

/**
 * A container component responsible for fetching and processing joke data,
 * then passing it to the presentational JokesSwipeWrapper component.
 */
const JokesSwipeWrapperContainer: FC<JokesSwipeWrapperContainerProps> = ({
                                                                           feedType = 'jokes',
                                                                         }) => {
  const { i18n, t } = useTranslation();

  // A memoized function to transform the raw API data into the format
  // required by the JokesSwipeWrapper component.
  const processJokesData = useCallback(
    (feedData: FeedQueryResult): Joke[] => {
      // Gracefully handle cases where the data structure is not as expected.
      if (!feedData?.news?.items) {
        return [];
      }

      return feedData.news.items
        .map((item) => {
          // Cast the generic item to our specific API joke type.
          const apiJoke = item as ApiJokeItem;

          // Safely access the nested joke data.
          const setupText = apiJoke.joke?.setup;
          const punchlineText = apiJoke.joke?.punchline;
          const id = apiJoke.uid;

          // If essential data is missing, we'll filter this item out.
          if (!id || !setupText || !punchlineText) {
            return null;
          }

          // Use the translation helper to get the text in the current language.
          const setup = getSSRTranslation(setupText, i18n);
          const punchline = getSSRTranslation(punchlineText, i18n);

          // Ensure we have valid text after translation.
          if (!setup || !punchline) {
            return null;
          }

          // Return the clean, structured joke object.
          return { id, setup, punchline };
        })
        .filter((joke): joke is Joke => joke !== null); // Type guard to filter out nulls.
    },
    [i18n]
  );

  // Use the generic data-fetching hook with our specific type and processor.
  const { data, loading, error } = useWidgetData<Joke>(
    feedType,
    processJokesData
  );

  // --- Render Logic ---

  // Display a loading state while data is being fetched.
  if (loading) {
    // In a real app, this could be a skeleton loader matching the component's shape.
    return <ActivityIndicator/>;
  }

  // Display an error message if the fetch fails.
  if (error) {
    return <div>{t('anErrorHasOccurred')}</div>;
  }

  // Pass the successfully fetched and processed jokes to the UI component.
  // The JokesSwipeWrapper itself handles the case where the `jokes` array is empty.
  return <JokesSwipeWrapper jokes={data} />;
};

export default JokesSwipeWrapperContainer;
